import { useEffect } from 'preact/hooks'
import { useAuth } from '../auth/useAuth'
import { useLocation } from 'preact-iso'
import { safeLocalStorage } from '@/utils/safeStorage'

interface ProtectedRouteProps {
  children: preact.ComponentChildren
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Save current URL for redirect after login. An Auth0 failure lands us on a ?error=… URL, and
      // returning someone to that after they finally sign in successfully just replays the failure
      // screen — so a URL carrying an error is never worth coming back to.
      if (!location.query.error) {
        safeLocalStorage.setItem('orbitalx-redirect-after-login', location.url)
      }
      // Redirect to login page if not authenticated
      location.route('/login')
    }
  }, [isAuthenticated, isLoading, location])

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-lg text-slate-600 dark:text-slate-300">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render children if not authenticated
  if (!isAuthenticated) {
    return null
  }

  return <>{children}</>
}
